import React, { useState, useRef } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { ScrollArea } from './ui/scroll-area';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CSVImportModal = ({ entityType, entityLabel, onSuccess, buttonVariant = "outline", buttonSize = "default" }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await api().get(`/csv/sample/${entityType}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sample_${entityType}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download sample CSV');
    }
  };

  const getImportEndpoint = () => {
    const masterDataTypes = ['university', 'program', 'country', 'city', 'intake', 'document_type', 'campus', 'degree_type', 'subject', 'faculty', 'application_status', 'application_type'];
    if (masterDataTypes.includes(entityType)) {
      return `/csv/import/master-data/${entityType}`;
    }
    return `/csv/import/${entityType}`;
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api().post(getImportEndpoint(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResults(response.data);

      if (response.data.success > 0) {
        toast.success(`Successfully imported ${response.data.success} ${entityLabel.toLowerCase()}`);
        if (onSuccess) onSuccess();
      }

      if (response.data.failed > 0 || response.data.duplicates > 0) {
        toast.warning(`${response.data.failed} failed, ${response.data.duplicates || 0} duplicates`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setResults({ success: 0, failed: 1, errors: [{ row: 0, error: error.response?.data?.detail || 'Import failed' }] });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFile(null);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Helper function to render error rows - extracted to avoid inline .map()
  const renderErrorRows = () => {
    if (!results?.errors || results.errors.length === 0) return null;
    const errorsToShow = results.errors.slice(0, 10);
    const items = [];
    for (let i = 0; i < errorsToShow.length; i++) {
      const err = errorsToShow[i];
      items.push(<p key={i}>Row {err.row}: {err.error}</p>);
    }
    return items;
  };

  // Helper function to render agent credentials - extracted to avoid inline .map()
  const renderAgentCredentials = () => {
    if (!results?.created_agents || results.created_agents.length === 0) return null;
    const items = [];
    for (let i = 0; i < results.created_agents.length; i++) {
      const agent = results.created_agents[i];
      items.push(
        <div key={i} className="p-2 bg-white rounded border">
          <p><strong>{agent.name}</strong></p>
          <p>Username: {agent.username}</p>
          <p>Password: {agent.password}</p>
        </div>
      );
    }
    return items;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); else setOpen(true); }}>
      <DialogTrigger asChild>
        <Button variant={buttonVariant} size={buttonSize} data-testid={`import-${entityType}-btn`}>
          <Upload className="w-4 h-4 mr-2" />
          Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Import {entityLabel}
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to bulk import {entityLabel.toLowerCase()}. Download the sample to see the required format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-center justify-between p-3 rounded-lg border border-dashed border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <Download className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">Need the format?</span>
            </div>
            <Button variant="link" size="sm" onClick={handleDownloadSample} className="text-primary" data-testid={`download-sample-${entityType}`}>
              Download Sample CSV
            </Button>
          </div>

          <div
            className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              file ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              data-testid={`csv-file-input-${entityType}`}
            />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileSpreadsheet className="w-8 h-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            ) : (
              <div>
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Click to select a CSV file or drag and drop</p>
              </div>
            )}
          </div>

          {results && (
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                {results.success > 0 && (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{results.success} imported</span>
                  </div>
                )}
                {results.duplicates > 0 && (
                  <div className="flex items-center gap-1 text-yellow-600">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">{results.duplicates} duplicates</span>
                  </div>
                )}
                {results.failed > 0 && (
                  <div className="flex items-center gap-1 text-red-600">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{results.failed} failed</span>
                  </div>
                )}
              </div>

              {results.errors && results.errors.length > 0 && (
                <Alert variant="destructive" className="bg-red-50">
                  <AlertDescription>
                    <ScrollArea className="h-[100px]">
                      <div className="space-y-1 text-sm">
                        {renderErrorRows()}
                        {results.errors.length > 10 && (
                          <p className="text-muted-foreground">...and {results.errors.length - 10} more errors</p>
                        )}
                      </div>
                    </ScrollArea>
                  </AlertDescription>
                </Alert>
              )}

              {results.created_agents && results.created_agents.length > 0 && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription>
                    <p className="font-medium mb-2">Created Agent Credentials:</p>
                    <ScrollArea className="h-[100px]">
                      <div className="space-y-2 text-sm font-mono">
                        {renderAgentCredentials()}
                      </div>
                    </ScrollArea>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {results ? 'Close' : 'Cancel'}
          </Button>
          {!results && (
            <Button onClick={handleImport} disabled={!file || importing} data-testid={`confirm-import-${entityType}`}>
              {importing ? (
                <span className="flex items-center">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </span>
              ) : (
                <span className="flex items-center">
                  <Upload className="w-4 h-4 mr-2" />
                  Import
                </span>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CSVImportModal;
